/**
 * Web hooks for fee schedule data
 * Using React hooks (useState/useEffect) - consistent with useBillingSettings
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface FeeScheduleRow {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean | null;
  context: string;
}

export interface FeeScheduleLineItem {
  fee_identifier: string;
  line_item_heading: string;
  fee_percentage: number | null;
  flat_amount_cents: number | null;
  min_amount_cents: number | null;
  fee_recipient: string;
  charge_effect: string;
  payout_effect: string;
  calculation_basis: string;
}

export interface FeeScheduleWithItems extends FeeScheduleRow {
  items: FeeScheduleLineItem[];
}

export interface CompanyFeeScheduleResult {
  schedule_id: string;
  schedule_name: string;
  schedule_context: string;
  items: FeeScheduleLineItem[];
}

/**
 * Hook: Fetch all active fee schedules (for dropdown options)
 */
export function useFeeSchedules(): {
  data: FeeScheduleRow[] | null;
  isLoading: boolean;
  error: Error | null;
} {
  const [data, setData] = useState<FeeScheduleRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const { data: schedules, error: queryError } = await supabase
          .from('fee_schedules')
          .select('id, name, description, is_default, context')
          .eq('is_active', true)
          .order('is_default', { ascending: false });

        if (queryError) {
          console.error('[useFeeSchedules] Error fetching schedules:', queryError);
          setError(new Error(queryError.message));
        } else {
          setData(schedules);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return { data, isLoading, error };
}

/**
 * Hook: Fetch active fee schedules with their line items, optionally filtered by context.
 * Runs two parallel queries (fee_schedules + fee_schedule_items) and merges them.
 */
export function useFeeSchedulesWithItems(context?: string): {
  data: FeeScheduleWithItems[] | null;
  isLoading: boolean;
  error: Error | null;
} {
  const [data, setData] = useState<FeeScheduleWithItems[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        // Build schedule query
        let scheduleQuery = supabase
          .from('fee_schedules')
          .select('id, name, description, is_default, context')
          .eq('is_active', true);

        if (context) {
          scheduleQuery = scheduleQuery.eq('context', context);
        }

        const [schedulesResult, itemsResult] = await Promise.all([
          scheduleQuery.order('is_default', { ascending: false }),
          supabase
            .from('fee_schedule_items')
            .select('fee_schedule_id, fee_identifier, line_item_heading, fee_percentage, flat_amount_cents, min_amount_cents, fee_recipient, charge_effect, payout_effect, calculation_basis, display_order')
            .order('display_order', { ascending: true }),
        ]);

        if (schedulesResult.error) {
          console.error('[useFeeSchedulesWithItems] Schedules error:', schedulesResult.error);
          setError(new Error(schedulesResult.error.message));
          return;
        }
        if (itemsResult.error) {
          console.error('[useFeeSchedulesWithItems] Items error:', itemsResult.error);
          setError(new Error(itemsResult.error.message));
          return;
        }

        const schedules = schedulesResult.data || [];
        const allItems = itemsResult.data || [];

        // Group items by schedule ID
        const itemsBySchedule = new Map<string, FeeScheduleLineItem[]>();
        for (const item of allItems) {
          const list = itemsBySchedule.get(item.fee_schedule_id) || [];
          list.push({
            fee_identifier: item.fee_identifier,
            line_item_heading: item.line_item_heading,
            fee_percentage: item.fee_percentage,
            flat_amount_cents: item.flat_amount_cents,
            min_amount_cents: item.min_amount_cents,
            fee_recipient: item.fee_recipient,
            charge_effect: item.charge_effect,
            payout_effect: item.payout_effect,
            calculation_basis: item.calculation_basis,
          });
          itemsBySchedule.set(item.fee_schedule_id, list);
        }

        // Merge
        const merged: FeeScheduleWithItems[] = schedules.map(s => ({
          ...s,
          items: itemsBySchedule.get(s.id) || [],
        }));

        setData(merged);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [context]);

  return { data, isLoading, error };
}

/**
 * Hook: Fetch a company's effective fee schedule (with line items)
 */
export function useCompanyFeeSchedule(companyId: string | null): {
  data: CompanyFeeScheduleResult | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [data, setData] = useState<CompanyFeeScheduleResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = useCallback(() => {
    setRefetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    if (!companyId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          'get_company_fee_schedule',
          { p_company_id: companyId },
        );

        if (rpcError) {
          console.error('[useCompanyFeeSchedule] RPC error:', rpcError);
          setError(new Error(rpcError.message || 'Failed to fetch fee schedule'));
        } else if (rpcData && Array.isArray(rpcData) && rpcData.length > 0) {
          // RPC returns rows with schedule_id, schedule_name, and line item fields
          const first = rpcData[0];
          const result: CompanyFeeScheduleResult = {
            schedule_id: first.schedule_id,
            schedule_name: first.schedule_name,
            schedule_context: first.schedule_context as string,
            items: rpcData.map((row: Record<string, unknown>) => ({
              fee_identifier: row.fee_identifier as string,
              line_item_heading: row.line_item_heading as string,
              fee_percentage: row.fee_percentage as number | null,
              flat_amount_cents: row.flat_amount_cents as number | null,
              min_amount_cents: row.min_amount_cents as number | null,
              fee_recipient: row.fee_recipient as string,
              charge_effect: row.charge_effect as string,
              payout_effect: row.payout_effect as string,
              calculation_basis: row.calculation_basis as string,
            })),
          };
          setData(result);
        } else {
          setData(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [companyId, refetchCount]);

  return { data, isLoading, error, refetch };
}
